// ─── OTURUM SAKLAMA ───
// "Beni hatırla" işaretliyse oturum localStorage'a yazılır → tarayıcı kapanıp açılsa da durur
// (token 30 gün geçerli). İşaretli değilse sessionStorage'a yazılır → sekme/tarayıcı kapanınca
// silinir (token zaten 24 saat). Ortak bilgisayarda "hatırlama" demek gerçekten hatırlamamak
// anlamına gelsin diye iki depo ayrı tutuluyor.
//
// Okurken önce sessionStorage'a bakılır: aynı tarayıcıda önce "hatırla"yıp sonra hatırlatmadan
// giren kullanıcıda taze oturum kazansın.
const readToken = () => sessionStorage.getItem('token') || localStorage.getItem('token');
const readUser = () => sessionStorage.getItem('user') || localStorage.getItem('user');

export const AuthService = {
    // Giriş sonrası kullanıcı bilgilerini kaydet.
    // remember=true → kalıcı (localStorage), false → yalnızca bu oturum (sessionStorage)
    saveSession: (token, user, remember = false) => {
        const store = remember ? localStorage : sessionStorage;
        const other = remember ? sessionStorage : localStorage;
        other.removeItem('token');
        other.removeItem('user');
        store.setItem('token', token);
        store.setItem('user', JSON.stringify(user));
    },

    // Oturumu tamamen temizle — iki depodan da
    clearSession: () => {
        ['token', 'user', 'selected_package', 'package_price', 'selectedPackage', 'active_profile_id'].forEach(
            key => { localStorage.removeItem(key); sessionStorage.removeItem(key); }
        );
        // Profil seçim bayrağı sekme oturumundadır; çıkışta sıfırla ki tekrar girişte profil sorulsun
        sessionStorage.removeItem('profile_session');
        // Profil token'ı bellekten temizle
        sessionStorage.removeItem('profile_token');
    },

    // Bakım geçişini de düşür: bakım sırasında hesaptan çıkan kişi yeniden bakım sayfasını
    // görsün (geçiş çerezi sunucuda tutulur, localStorage temizliği ona dokunmaz).
    // Bakım kapalıyken de zararsızdır. Ağ hatası çıkışı ENGELLEMEZ — en fazla 1,5 sn beklenir.
    endMaintenancePass: () => {
        const base = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const req = fetch(`${base}/api/bakim/cikis`, { method: 'POST', credentials: 'include', keepalive: true })
            .catch(() => {});
        return Promise.race([req, new Promise(r => setTimeout(r, 1500))]);
    },

    getToken: () => readToken(),

    // Kalıcı cihaz kimliği (eş-zamanlı cihaz limiti için). Tarayıcı/cihaz başına bir kez üretilir.
    getDeviceId: () => {
        let id = localStorage.getItem('filbox_device_id');
        if (!id) {
            id = (window.crypto?.randomUUID?.() || `dev_${Date.now()}_${Math.random().toString(36).slice(2)}`);
            localStorage.setItem('filbox_device_id', id);
        }
        return id;
    },

    // Ham fetch'ler için istek başlıkları: token + aktif profil (X-Profile-Id) + profil token.
    // Aktif profil başlığı backend'de profil-bazlı geçmiş/liste ve çocuk içerik
    // filtresi için kullanılır (api.js/authFetch bunu zaten ekler; oynatıcı/detay
    // gibi ham fetch kullanan yerler bu yardımcıyı kullanır).
    authHeaders: (extra = {}) => {
        const token = readToken();
        const profileId = localStorage.getItem('active_profile_id');
        const profileToken = sessionStorage.getItem('profile_token');
        return {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(profileId ? { 'X-Profile-Id': profileId } : {}),
            ...(profileToken ? { 'X-Profile-Token': profileToken } : {}),
            ...extra,
        };
    },

    // Profil token — verify-pin sonrası backend'den gelir, sessionStorage'da tutulur.
    // sessionStorage: sekme kapanınca / sayfa yenilenince silinir → PIN tekrar sorulur (Netflix davranışı).
    saveProfileToken: (token) => sessionStorage.setItem('profile_token', token),
    getProfileToken: () => sessionStorage.getItem('profile_token'),
    clearProfileToken: () => sessionStorage.removeItem('profile_token'),

    getUser: () => {
        // Bozuk/elle değiştirilmiş localStorage verisi uygulamayı çökertmesin
        try {
            const u = readUser();
            return u ? JSON.parse(u) : null;
        } catch {
            localStorage.removeItem('user');
            sessionStorage.removeItem('user');
            return null;
        }
    },

    isLoggedIn: () => !!readToken(),

    // Abonelik durumu — hem düz (/me) hem iç içe (login) kullanıcı yapısından okur.
    // subscribed = aktif VE deneme (trial) değil → yani ödeme yapıp paket almış kullanıcı.
    getSubscriptionState: () => {
        const u = AuthService.getUser();
        if (!u) return { active: false, isTrial: false, subscribed: false };
        const expiresAt = u.subscriptionExpiresAt ?? u.subscription?.expiresAt ?? null;
        const isTrial = u.subscriptionIsTrial ?? u.subscription?.isTrial ?? false;
        const active = expiresAt ? new Date() < new Date(expiresAt) : false;
        return { active, isTrial, subscribed: active && !isTrial };
    },

    // Admin mi? (Yalnızca arayüz gösterimi için — asıl yetki kontrolü backend'de)
    isAdmin: () => {
        return AuthService.getUser()?.roles?.includes('admin') || false;
    }
};